// /team/champion: help queue for Champion AI + weekly digest for the owner.
// Chat contents never appear here. Topic, Program name, and a short note only.
import React, { useCallback, useEffect, useState } from 'react';
import { Theme, themeColors } from '../theme/colors';
import {
  TeamMe,
  HelpQueuePayload,
  HelpRequestRow,
  AdoptionDigest,
  TeamError,
  TeamUnauthorizedError,
  getHelpQueue,
  replyHelpRequest,
  closeHelpRequest,
  getAdoptionDigest,
  sendAdoptionDigest,
} from './chat/teamService';
import TeamGuide from './TeamGuide';

export interface TeamChampionProps {
  theme: Theme;
  me: TeamMe;
}

const TOPIC_LABEL: Record<string, string> = {
  misi: 'Misi',
  playbook: 'Playbook',
  kredit: 'Kredit',
  lainnya: 'Lainnya',
};

const TeamChampion: React.FC<TeamChampionProps> = ({ theme, me }) => {
  const c = themeColors[theme];
  const isOwner = me.role === 'owner';

  const [queue, setQueue] = useState<HelpQueuePayload | null>(null);
  const [digest, setDigest] = useState<AdoptionDigest | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState<'open' | 'answered' | ''>('open');

  const cardStyle: React.CSSProperties = {
    background: c.panel,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '18px',
  };
  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: c.ink3,
    margin: '22px 0 10px',
  };
  const ghostBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '7px 12px',
    fontSize: '12.5px',
    color: c.ink,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const primaryBtnStyle: React.CSSProperties = {
    ...ghostBtnStyle,
    background: c.jadeDim,
    borderColor: c.jade,
    fontWeight: 600,
  };

  const describe = (err: unknown) => {
    if (err instanceof TeamUnauthorizedError) return '';
    if (err instanceof TeamError) return err.message;
    return 'Gagal memuat antrian Champion.';
  };

  const load = useCallback(async () => {
    setError('');
    try {
      const next = await getHelpQueue(filter || undefined);
      setQueue(next);
      if (isOwner) {
        try {
          setDigest(await getAdoptionDigest());
        } catch {
          setDigest(null);
        }
      }
      setStatus('ready');
    } catch (err) {
      if (err instanceof TeamError && err.status === 403) {
        setStatus('forbidden');
        return;
      }
      const msg = describe(err);
      if (msg) setError(msg);
      setStatus('error');
    }
  }, [filter, isOwner]);

  useEffect(() => { void load(); }, [load]);

  const handleReply = async (row: HelpRequestRow) => {
    const text = (replyDrafts[row.id] || '').trim();
    if (!text) return;
    setBusy(true);
    setError('');
    try {
      await replyHelpRequest(row.id, text);
      setReplyDrafts((prev) => ({ ...prev, [row.id]: '' }));
      setNotice('Balasan terkirim. Anggota mendapat Pengingat di aplikasi.');
      await load();
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async (row: HelpRequestRow) => {
    setBusy(true);
    try {
      await closeHelpRequest(row.id);
      setNotice('Permintaan ditutup.');
      await load();
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSendDigest = async () => {
    setBusy(true);
    try {
      const sent = await sendAdoptionDigest();
      setDigest(sent);
      setNotice(
        sent.sent
          ? 'Digest minggu ini dikirim ke Pengingatmu.'
          : 'Digest minggu ini sudah pernah dikirim.',
      );
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading') {
    return <div style={{ fontSize: '13px', color: c.ink2 }}>Memuat antrian Champion…</div>;
  }

  if (status === 'forbidden') {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: c.ink }}>Champion AI</div>
        <div style={{ fontSize: '13px', color: c.ink2, marginTop: '8px', lineHeight: 1.65 }}>
          Antrian ini untuk Champion AI, Admin, dan pemilik. Minta pemilik menetapkan role Champion AI di Grup.
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={sectionLabelStyle}>Champion AI</div>
      {error && <div style={{ fontSize: '12.5px', color: theme === 'light' ? '#DC2626' : '#F87171', marginBottom: '10px' }}>{error}</div>}
      {notice && <div style={{ fontSize: '12.5px', color: c.ink2, marginBottom: '10px' }}>{notice}</div>}
      <div style={cardStyle}>
        <TeamGuide
          theme={theme}
          lead={[
            'Anggota menekan Saya butuh bantuan dari Misi. Champion menjawab di sini. Isi chat tetap privat.',
            'Digest mingguan merangkum adopsi untuk pemilik: siapa belum mencoba, siapa minta tolong, apakah sudah ada Champion.',
          ]}
          exampleName="Contoh"
          exampleBody={[
            'Sari macet di Misi laporan kunjungan. Dia tidak menempelkan chat. Dia pilih topik Misi dan tulis satu kalimat.',
            'Raka, Champion AI, membalas: mulai dari Playbook laporan. Sari dapat Pengingat. Admin tidak perlu membuka percakapannya.',
          ]}
        />
      </div>

      {isOwner && digest && (
        <>
          <div style={sectionLabelStyle}>Digest minggu ini</div>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: c.ink2, lineHeight: 1.7 }}>
              {digest.wau} aktif 7 hari · {digest.joined_unactivated} belum mencoba · {digest.open_help} minta bantuan · {digest.champion_count} Champion
            </div>
            <button
              type="button"
              onClick={handleSendDigest}
              disabled={busy}
              className="pa-ghost"
              style={{ ...ghostBtnStyle, marginTop: '12px', opacity: busy ? 0.45 : 1 }}
            >
              Kirim digest minggu ini
            </button>
          </div>
        </>
      )}

      <div style={sectionLabelStyle}>Antrian bantuan</div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {([
          { id: 'open', label: 'Terbuka' },
          { id: 'answered', label: 'Sudah dijawab' },
          { id: '', label: 'Semua' },
        ] as const).map((item) => (
          <button
            key={item.id || 'all'}
            type="button"
            onClick={() => setFilter(item.id)}
            className="pa-ghost"
            style={{
              ...ghostBtnStyle,
              background: filter === item.id ? c.jadeDim : 'none',
              borderColor: filter === item.id ? c.jade : c.line2,
              fontWeight: filter === item.id ? 600 : 500,
            }}
          >
            {item.label}
            {item.id === 'open' && queue ? ` · ${queue.open_count}` : ''}
          </button>
        ))}
      </div>
      {(queue?.requests || []).length === 0 && (
        <div style={cardStyle}>
          <div style={{ fontSize: '13px', color: c.ink2 }}>Tidak ada permintaan di filter ini.</div>
        </div>
      )}
      {(queue?.requests || []).map((row) => (
        <div key={row.id} style={{ ...cardStyle, marginTop: '10px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: c.ink }}>
            {row.user_email || 'Anggota'} · {TOPIC_LABEL[row.topic] || row.topic}
          </div>
          <div style={{ fontSize: '12px', color: c.ink3, marginTop: '6px' }}>
            {row.program_name ? `Program ${row.program_name}` : 'Tanpa Program'}
            {row.mission_name ? ` · Misi ${row.mission_name}` : ''}
            {row.status === 'open' ? ' · Terbuka' : row.status === 'answered' ? ' · Dijawab' : ' · Ditutup'}
          </div>
          {row.note && (
            <div style={{ fontSize: '13px', color: c.ink2, marginTop: '10px', lineHeight: 1.6 }}>{row.note}</div>
          )}
          {row.reply_text && (
            <div style={{ fontSize: '13px', color: c.ink, marginTop: '10px', lineHeight: 1.6 }}>
              Balasan{row.answered_email ? ` · ${row.answered_email}` : ''}: {row.reply_text}
            </div>
          )}
          {row.status === 'open' && (
            <>
              <textarea
                value={replyDrafts[row.id] || ''}
                onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                placeholder="Balasan singkat. Jangan minta mereka menempelkan chat."
                rows={3}
                className="pa-field"
                style={{
                  width: '100%',
                  marginTop: '12px',
                  resize: 'vertical',
                  background: c.panel2,
                  border: `1px solid ${c.line2}`,
                  borderRadius: '8px',
                  padding: '10px 12px',
                  color: c.ink,
                  fontFamily: 'inherit',
                  fontSize: '13px',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                <button type="button" onClick={() => handleReply(row)} disabled={busy} className="pa-primary" style={{ ...primaryBtnStyle, opacity: busy ? 0.45 : 1 }}>
                  Kirim bantuan
                </button>
                <button type="button" onClick={() => handleClose(row)} disabled={busy} className="pa-ghost" style={ghostBtnStyle}>
                  Tutup
                </button>
              </div>
            </>
          )}
          {row.status === 'answered' && (
            <button type="button" onClick={() => handleClose(row)} disabled={busy} className="pa-ghost" style={{ ...ghostBtnStyle, marginTop: '12px' }}>
              Tutup
            </button>
          )}
        </div>
      ))}
    </>
  );
};

export default TeamChampion;
